import { Box, Flex, Skeleton, Text } from "@chakra-ui/react";
import useAssembleia from "core/features/assembleia/hooks/useAssembleia";
import useParticipantesAssembleia from "core/features/assembleia/hooks/useParticipantesAssembleia";
import useParticipantesAssembleiaListaCompleta from "core/features/assembleia/hooks/useParticipantesAssembleiaListaCompleta";
import { VotacaoParticipanteItem } from "core/features/assembleia/typings";
import { useBreadcrumb } from "core/features/breadcrumbs";
import colors from "core/resources/theme/colors";
import InputSearchWhite from "ui/components/Input/InputSearch/InputSearchWhite";
import { TabsDetalheAssembleiaRoutes } from "../../typings";
import FiltroRangeLetras from "./components/FiltroRangeLetras";
import ListaParticipantes from "./components/ListaParticipantes";
import useFiltroParticipantes from "./hooks/useFiltroParticipantes";
import OutlineButton from "ui/components/Button/OutlineButton";
import ModalAssembleiaParticipantes from "./components/ModalAssembleiaParticipantes";
import { useState, useMemo, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { FaPrint } from "react-icons/fa";
import { SiMicrosoftexcel } from "react-icons/si";
import Container from "components/Container";
import { useLoading } from "components/GlobalLoading";
import { exportExcelVotacaoParticipantes } from "./helper";
import InfiniteScrollLoader from "components/InfiniteScrollLoader";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFListaParticipantes from "./components/PDFListaParticipantes";

interface AssembleiaparticipantesProps {
  idAssembleia: string;
}

function AssembleiaParticipantes({
  idAssembleia,
}: AssembleiaparticipantesProps) {
  const { filtro, onChangeFiltro } = useFiltroParticipantes();

  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    data: detalheAssembleia,
    isLoading,
    isFetching,
  } = useAssembleia(idAssembleia);

  const {
    data,
    isLoading: isLoadingParticipantes,
    isFetching: isFetchingParticipantes,
    isFetchingNextPage,
    refetch: refetchParticipantes,
    fetchNextPage,
  } = useParticipantesAssembleia(idAssembleia, filtro);

  const { isLoading: isLoadingListaCompleta, mutate: buscarListaCompleta } =
    useParticipantesAssembleiaListaCompleta(idAssembleia);

  const totalPages = data?.pages[data?.pages.length - 1].meta.totalPages ?? 0;

  const hasNextPage = totalPages > data?.pages.length!;

  const _isLoading =
    isLoading ||
    isFetching ||
    (isFetchingParticipantes && !isFetchingNextPage) ||
    isLoadingListaCompleta;

  useBreadcrumb([
    { name: "Votações", url: "/administracao/assembleias" },
    {
      name:
        _isLoading || !detalheAssembleia
          ? "Carregando"
          : detalheAssembleia.nome,
      url: `/administracao/assembleias/${idAssembleia}/`,
    },
    {
      name: "Participantes",
      url: `/administracao/assembleias/${idAssembleia}?opcao=${TabsDetalheAssembleiaRoutes.Participantes}`,
    },
  ]);

  const handleModalClose = () => {
    setIsOpenModal(false);
  };

  const handleSuccess = () => {
    setIsOpenModal(false);

    refetchParticipantes();
  };

  const participantes = useMemo(() => {
    return (
      data?.pages.reduce(
        (agg: VotacaoParticipanteItem[], item) => agg.concat(item.items ?? []),
        []
      ) ?? []
    );
  }, [data]);

  const { onDownload: _onExportPdf } = useDownloadRenderedPdf({
    fileName: `Lista de participantes - ${detalheAssembleia?.nome}`,
    Component: PDFListaParticipantes,
    loading: isLoading,
  });

  const handlePrint = useCallback(() => {
    if (!detalheAssembleia) return;

    buscarListaCompleta(undefined, {
      onSuccess: ({ items }) => {
        _onExportPdf({
          participantes: items,
          votacaoNome: detalheAssembleia!.nome,
        });
      },
    });
  }, [_onExportPdf, buscarListaCompleta, detalheAssembleia]);

  const handleExcel = useCallback(() => {
    buscarListaCompleta(undefined, {
      onSuccess: ({ items }) => {
        exportExcelVotacaoParticipantes({
          nomeVotacao: detalheAssembleia?.nome!,
          data: items,
        });
      },
    });
  }, [buscarListaCompleta, detalheAssembleia?.nome]);

  useLoading(_isLoading);

  return (
    <>
      <Container pl={0} pb={2} className="hidden-print">
        <Box mb={6}>
          <FiltroRangeLetras
            selectedRange={filtro.prefixos}
            onChangeRange={(prefixos) =>
              onChangeFiltro({
                prefixos: prefixos === filtro.prefixos ? undefined : prefixos,
                page: 1,
                search: "",
              })
            }
          />
        </Box>

        <Flex gridGap={4} alignItems="center" mb={6} flexWrap="wrap">
          <Flex gridGap={4} alignItems="center" flexWrap="wrap">
            <Text fontSize={18} color={colors.black}>
              Participantes
            </Text>
            <OutlineButton
              bgColor="transparent"
              minW="fit-content"
              onClick={() => setIsOpenModal(true)}
            >
              Cadastrar votante
            </OutlineButton>

            <OutlineButton
              bgColor="transparent"
              minW="fit-content"
              isLoading={
                isLoadingParticipantes ||
                isFetchingNextPage ||
                isLoadingListaCompleta
              }
              onClick={handlePrint}
            >
              <FaPrint /> Imprimir PDF
            </OutlineButton>

            <OutlineButton
              bgColor="transparent"
              minW="fit-content"
              isLoading={
                isLoadingParticipantes ||
                isFetchingNextPage ||
                isLoadingListaCompleta
              }
              onClick={handleExcel}
            >
              <SiMicrosoftexcel /> Exportar Excel
            </OutlineButton>
          </Flex>

          {isOpenModal ? (
            <ModalAssembleiaParticipantes
              isOpen={isOpenModal}
              onClose={handleModalClose}
              onSaved={handleSuccess}
              votacaoId={idAssembleia}
            />
          ) : null}

          <Box flex={1} minW="340px">
            <InputSearchWhite
              placeholder="Pesquisar por nome do morador ou unidade"
              onSearch={(search) =>
                onChangeFiltro({ search, page: 1, prefixos: "" })
              }
              defaultValue={filtro.search || ""}
              maxW="100%"
            />
          </Box>
        </Flex>

        {isLoadingParticipantes ? (
          <Flex gridGap={3} flexDir="column">
            <LoadingSkeleton />
            <LoadingSkeleton />
            <LoadingSkeleton />
            <LoadingSkeleton />
            <LoadingSkeleton />
          </Flex>
        ) : (
          <InfiniteScroll
            dataLength={participantes.length}
            next={() => {
              fetchNextPage();
            }}
            hasMore={!!hasNextPage}
            loader={isFetchingNextPage ? <InfiniteScrollLoader /> : null}
          >
            <ListaParticipantes
              participantes={participantes}
              votacaoId={idAssembleia}
            />
          </InfiniteScroll>
        )}
      </Container>
    </>
  );
}

const LoadingSkeleton = () => <Skeleton w="100%" rounded={16} h="70px" />;

export default AssembleiaParticipantes;
